import { useEffect, useState } from 'react'
import api from '@/lib/axiosInstance'
import type { IntakeSessionSummary } from './aiIntake'

/** Still has listings to create, or is waiting on the agent's answer. */
export const isUnfinished = (s: IntakeSessionSummary) => s.status === 'active' && !s.ignored && (s.draftCount === 0 || s.completedCount < s.draftCount)

export const relativeTime = (iso: string | null) => {
    if (!iso) return ''
    const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60000)
    if (minutes < 1) return 'just now'
    if (minutes < 60) return `${minutes} min ago`
    const hours = Math.round(minutes / 60)
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`
    const days = Math.round(hours / 24)
    return days < 7 ? `${days} day${days === 1 ? '' : 's'} ago` : new Date(iso).toLocaleDateString()
}

/** "2 of 3 listings still to create", "Waiting for your answer", "All created". */
export const progressLabel = (s: IntakeSessionSummary) => {
    if (s.ignored) return 'Unrelated upload skipped'
    if (s.draftCount === 0) return 'Waiting for your answer'
    const left = s.draftCount - s.completedCount
    if (left <= 0) return `${s.draftCount} listing${s.draftCount === 1 ? '' : 's'} created`
    return `${left} of ${s.draftCount} listing${s.draftCount === 1 ? '' : 's'} still to create`
}

/**
 * The agent's AI listing conversations, newest first: the Draft tab shows the
 * unfinished ones as rows, and the AI page lists them all to switch between.
 * `reloadKey` refetches when it changes, e.g. after a new turn.
 */
export function useAIConversations(reloadKey?: unknown) {
    const [sessions, setSessions] = useState<IntakeSessionSummary[] | null>(null)

    useEffect(() => {
        let cancelled = false
        const load = async () => {
            const all: IntakeSessionSummary[] = []
            let offset = 0
            do {
                const { data } = await api.get('/listings/ai/sessions', { params: { limit: 50, offset } })
                if (cancelled) return
                if (!data.status) throw new Error('Could not load conversations')
                all.push(...data.sessions)
                setSessions([...all])
                if (!data.hasMore) break
                offset += 50
            } while (!cancelled)
        }
        load().catch(() => !cancelled && setSessions((old) => old ?? []))
        return () => {
            cancelled = true
        }
    }, [reloadKey])

    return { sessions: sessions ?? [], loaded: sessions !== null }
}
